import React, { useState, useEffect, useCallback } from 'react'; // useRef'i kaldır
import { createPortal } from 'react-dom';
import { Sliders, Sun, Mail, X, Calculator, Info } from 'lucide-react';
import EnergyTransfer from '../animations/EnergyTransfer';
import EmailReportModal from '../modals/EmailReportModal';
import { sendReportByEmail } from '../../services/emailService';
import { generateSystemReport } from '../../utils/reportGenerator';
import { 
  calculateSystemResults,
  calculateCoolingFromHeating,
  calculateHeatingFromCooling,
  calculateCOP
} from '../../utils/heatPumpCalculations';
import { CLIENT_EQUIPMENT_TYPES } from '../../utils/constants';
import DebugPopup from '../debug/DebugPopup';

// Sadece gerekli Card bileşenini bırakalım, diğerlerini kaldıralım
const Card = ({ className, children }) => (
  <div className={`rounded-lg shadow-md ${className}`}>{children}</div>
);

// Öncelikle global değişkenleri kaldırın ve bir state ile değiştirin
// let globalEuroRate = null;
// let isEuroFetchInProgress = false;

const EquipmentCard = ({ 
  system, onRemove, onParamChange, darkMode, solarEnabled, setSolarEnabled, globalParams 
}) => {
  const { 
    // id değişkenini kullanmadığımız için buradan kaldıralım
    // id,  
    name, 
    type, 
    results,
    heatingCapacity = 500,
    heatingTemperature = 60, 
    coolingCapacity = 500,
    coolingTemperature = 20, 
    chillerCop = 6.5,
    workingHours = 5000,
    clientEquipmentType = 'boiler'
  } = system;
  
  const [showParams, setShowParams] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTreeInfo, setShowTreeInfo] = useState(false);
  const [treeInfoPos, setTreeInfoPos] = useState(null);
  const [showInvestCostInfo, setShowInvestCostInfo] = useState(false);
  const [investCostInfoPos, setInvestCostInfoPos] = useState(null);
  const [showPaybackInfo, setShowPaybackInfo] = useState(false);
  const [paybackInfoPos, setPaybackInfoPos] = useState(null);
  const [eurRate, setEurRate] = useState(null);
  const [debugPopupOpen, setDebugPopupOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // CLIENT_EQUIPMENT_TYPES'dan gelen clientEquipmentType için varsayılan değeri al
  // Bu değişken tanımını ekleyin - cardId zaten var ve çalışıyor
  const clientEquipment = CLIENT_EQUIPMENT_TYPES.find(item => item.id === clientEquipmentType) || CLIENT_EQUIPMENT_TYPES[0];
  
  // E-posta rapor modalini açma fonksiyonu
  const handleSendReport = () => {
    setShowEmailModal(true);
  };
  
  // E-posta rapor modalini kapatma fonksiyonu  
  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
  };
  
  // E-posta gönderme fonksiyonu
  const handleSendEmail = async (email, name) => {
    try {
      setIsLoading(true);
      
      // Rapor verilerini hazırla
      const reportData = {
        name: system.name,
        type: type,
        heatingCapacity,
        coolingCapacity,
        heatingTemperature,
        coolingTemperature,
        workingHours,
        chillerCop,
        solarEnabled,
        results,
        eurRate
      };
      
      // Rapor PDF'ini oluştur (Blob olarak döner)
      const pdfBlob = await generateSystemReport(reportData, solarEnabled, globalParams);
      
      // İndirme işlemi mi yoksa e-posta mı kontrol et
      if (email === 'download') {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${system.name}_Rapor.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setShowEmailModal(false);
        return; // E-posta gönderme işlemini atla
      }

      // E-posta gönder
      await sendReportByEmail({
        recipientEmail: email,
        recipientName: name,
        systemName: system.name,
        pdfBlob: pdfBlob // PDF Blob'u buraya gönderiyoruz
      });
      
      console.log("Rapor başarıyla e-posta ile gönderildi");
      setShowEmailModal(false);
      
      // Başarı bildirimi eklenebilir
      alert("Rapor başarıyla e-posta adresinize gönderildi!");
      
    } catch (error) {
      console.error("E-posta gönderme hatası:", error);
      alert("E-posta gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Kullanıcı ekipmanı değiştirme fonksiyonu
  const handleClientEquipmentChange = (equipmentId) => {
    memoizedOnParamChange('clientEquipmentType', equipmentId);
  };
  
  // Parametre değişikliğini önbelleğe al
  const memoizedOnParamChange = useCallback((paramName, value) => {
    onParamChange(system.id, paramName, value);
  }, [system.id, onParamChange]);
  
  // Debug popup için parametre değişikliklerini işleyen fonksiyon
  const handleParamChange = useCallback((paramName, value) => {
    memoizedOnParamChange(paramName, value);
    
    // Popup açıkken değerleri anında güncelle
    if (debugPopupOpen) {
      // Parametre değişikliğini system objesine önden yansıtalım
      const updatedSystem = { ...system, [paramName]: value };
      const calculatedResults = calculateSystemResults(updatedSystem, globalParams, solarEnabled);
      
      setDebugData({
        system: updatedSystem,
        globalParams,
        solarEnabled,
        eurRate,
        results: calculatedResults
      });
    }
  }, [system, memoizedOnParamChange, debugPopupOpen, globalParams, solarEnabled, eurRate]);

  // Solar toggle fonksiyonu - tek yerde güncelleme yapıp diğer her şeyi App.js'in hallettmesini sağlayalım
  const handleSolarToggle = () => {
    // Yeni değeri hesapla
    const newSolarValue = !solarEnabled;
    
    // Console.log ekleyelim
    console.log(`🔄 Solar toggle: ${solarEnabled} -> ${newSolarValue}`);
    
    // Sadece global state'i güncelle, diğer hesaplamaları iptal et
    setSolarEnabled(newSolarValue);
    
    // Mevcut state ile yeni sonuçları hemen hesapla (debug için)
    const newResults = calculateSystemResults(system, globalParams, newSolarValue);
    console.log("🌞 Solar değişikliği ile yeni CO2:", newResults.co2);
  };

  // Debug verilerini hazırlama fonksiyonu
  const prepareDebugData = () => {
    setDebugData({
      system: {
        heatingCapacity,
        coolingCapacity,
        heatingTemperature,
        coolingTemperature,
        chillerCop,
        workingHours
      },
      globalParams,
      solarEnabled, // Bu değerin doğru olduğundan emin olun
      eurRate,
      results: system.results || {}
    });
    setDebugPopupOpen(true);
  };
  
  // Debug popup verilerini güncelleme - parametreler değiştiğinde çalışır
  useEffect(() => {
    // Eğer popup açıksa, verileri güncelle
    if (debugPopupOpen) {
      const calculatedResults = calculateSystemResults(system, globalParams, solarEnabled);
      
      setDebugData({
        system,
        globalParams,
        solarEnabled,
        eurRate,
        results: calculatedResults
      });
    }
  }, [
    debugPopupOpen,
    system.heatingCapacity,
    system.coolingCapacity,
    system.heatingTemperature,
    system.coolingTemperature,
    system.chillerCop,
    system.workingHours,
    solarEnabled,
    globalParams,
    system,
    eurRate
  ]);

 
 
  
  // Kur değiştiğinde eurRate'i güncelle (globalParams.eurRate değiştiğinde)
  useEffect(() => {
    // Eğer globalParams'da euro kuru varsa kullan
    if (globalParams?.eurRate) {
      setEurRate(globalParams.eurRate);
    }
  }, [globalParams?.eurRate, setEurRate]); // globalParams.eurRate'i bağımlılık olarak ekledik

  // Ağaç kazanımı bilgi balonu: dışarı tıklayınca, scroll'da veya Escape tuşunda kapat
  useEffect(() => {
    if (!showTreeInfo) return;

    const handleClickOutside = (event) => {
      const tooltipEl = document.getElementById(`tree-info-tooltip-${system.id}`);
      const buttonEl = document.getElementById(`tree-info-button-${system.id}`);
      if (
        tooltipEl && !tooltipEl.contains(event.target) &&
        buttonEl && !buttonEl.contains(event.target)
      ) {
        setShowTreeInfo(false);
        setTreeInfoPos(null);
      }
    };

    // Listener'ı bir frame geciktir ki açılış tıklaması tetiklemesin
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setShowTreeInfo(false);
        setTreeInfoPos(null);
      }
    };

    const handleScroll = () => {
      setShowTreeInfo(false);
      setTreeInfoPos(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showTreeInfo, system.id]);

  // Yatırım maliyeti bilgi balonu: dışarı tıklayınca, scroll'da veya Escape tuşunda kapat
  useEffect(() => {
    if (!showInvestCostInfo) return;

    const handleClickOutside = (event) => {
      const tooltipEl = document.getElementById(`invest-cost-tooltip-${system.id}`);
      const buttonEl = document.getElementById(`invest-cost-info-button-${system.id}`);
      if (
        tooltipEl && !tooltipEl.contains(event.target) &&
        buttonEl && !buttonEl.contains(event.target)
      ) {
        setShowInvestCostInfo(false);
        setInvestCostInfoPos(null);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setShowInvestCostInfo(false);
        setInvestCostInfoPos(null);
      }
    };

    const handleScroll = () => {
      setShowInvestCostInfo(false);
      setInvestCostInfoPos(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showInvestCostInfo, system.id]);

  // Geri ödeme süresi bilgi balonu: dışarı tıklayınca, scroll'da veya Escape tuşunda kapat
  useEffect(() => {
    if (!showPaybackInfo) return;

    const handleClickOutside = (event) => {
      const tooltipEl = document.getElementById(`payback-tooltip-${system.id}`);
      const buttonEl = document.getElementById(`payback-info-button-${system.id}`);
      if (
        tooltipEl && !tooltipEl.contains(event.target) &&
        buttonEl && !buttonEl.contains(event.target)
      ) {
        setShowPaybackInfo(false);
        setPaybackInfoPos(null);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setShowPaybackInfo(false);
        setPaybackInfoPos(null);
      }
    };

    const handleScroll = () => {
      setShowPaybackInfo(false);
      setPaybackInfoPos(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showPaybackInfo, system.id]);


  // Card tipi ve görsel efektleri belirleme
  let equipmentName = '';
  let imgSrc = '';
  let bgGradient = '';
  let borderColor = '';
  
  // Kart tipini belirleme ve renk sınıflarını güncelleme
  switch(type) {
    case 'chiller':
      equipmentName = 'Chiller';
      imgSrc = '/icons/chiller.png';
      bgGradient = 'from-[#5B9BD5] to-[#2E75B6] dark:from-[#1F4E79]/30 dark:to-[#1F4E79]/30';
      borderColor = 'border-[#2E75B6] dark:border-[#1F4E79]';
      break;
    case 'heatRecovery':
      equipmentName = 'Atık Isı';
      imgSrc = '/icons/waste-heat.png';
      bgGradient = 'from-[#7030A0]/20 to-[#7030A0]/40 dark:from-[#7030A0]/30 dark:to-[#7030A0]/30';
      borderColor = 'border-[#7030A0] dark:border-[#7030A0]/70';
      break;
    case 'coolingTower':
      equipmentName = 'Soğutma Kulesi';
      imgSrc = '/icons/cooling-tower.png';
      bgGradient = 'from-[#00B0F0]/20 to-[#00B0F0]/40 dark:from-[#00B0F0]/30 dark:to-[#00B0F0]/30';
      borderColor = 'border-[#00B0F0] dark:border-[#00B0F0]/70';
      break;
    default:
      equipmentName = 'Ekipman';
      imgSrc = '/icons/equipment.png';
      bgGradient = 'from-gray-100 to-gray-300 dark:from-gray-900/30 dark:to-gray-800/30';
      borderColor = 'border-gray-300 dark:border-gray-700';
  }
  

  // Isıtma kapasitesi değiştiğinde soğutma kapasitesini güncelle
const handleHeatingCapacityChange = (newHeatingCapacity) => {
  // Isıtma kapasitesini güncelle
  handleParamChange('heatingCapacity', newHeatingCapacity);
  
  // Demo sayfasındaki gibi heatPumpCop hesapla
  const heatPumpCop = calculateCOP(coolingTemperature, heatingTemperature, chillerCop);
  
  // Demo koddaki formülle soğutma kapasitesini güncelle
  const newCoolingCapacity = calculateCoolingFromHeating(newHeatingCapacity, heatPumpCop);
  
  // Soğutma kapasitesini güncelle
  handleParamChange('coolingCapacity', newCoolingCapacity);
};

// Soğutma kapasitesi değiştiğinde ısıtma kapasitesini güncelle
const handleCoolingCapacityChange = (newCoolingCapacity) => {
  // Soğutma kapasitesini güncelle
  handleParamChange('coolingCapacity', newCoolingCapacity);
  
  // Demo sayfasındaki gibi heatPumpCop hesapla
  const heatPumpCop = calculateCOP(coolingTemperature, heatingTemperature, chillerCop);
  
  // Demo koddaki formülle ısıtma kapasitesini güncelle
  const newHeatingCapacity = calculateHeatingFromCooling(newCoolingCapacity, heatPumpCop);
  
  // Isıtma kapasitesini güncelle
  handleParamChange('heatingCapacity', newHeatingCapacity);
};
 

  return (
    <>
      <style>
        {`
          @keyframes slowPulse {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 0.5; }
          }
          .animate-pulse-slow {
            animation: slowPulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          .card-transition {
            transition: all 0.8s ease-in-out;
          }
          
          .hover-rotate {
            transition: transform 0.6s ease;
          }
          
          .hover-rotate:hover {
            transform: rotate(0deg);
          }
        `}
      </style>

      <Card className={`mb-4 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-200'}`}>
        <div className={`relative ${darkMode ? 'bg-gray-900' : 'bg-gray-200'} shadow-xl rounded-lg p-4 overflow-hidden transform transition-all duration-300 hover:shadow-2xl w-full`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${bgGradient} opacity-20 animate-pulse-slow`}></div>
          
          {/* Başlık ve butonlar */}
          <div className="relative z-10 mb-4">
            <div className="flex justify-between items-start">
              {/* Başlık kısmı */}
              <div className={`pb-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} tracking-tight`}>
                  {type === 'chiller' 
                    ? `Soğutma Sisteminden Isıtma - ${name.split(' ').pop()}` 
                    : name
                  }
                </h2>
              </div>
              
              {/* Dokunmatik ekrana uygun butonlar */}
              <div className="flex items-center gap-6">
                {/* E-posta butonu */}
                <button
                  onClick={handleSendReport}
                  className={`p-2.5 rounded-full transition-all duration-500 ${
                    darkMode ? 'bg-[#70AD47]/70 text-gray-200' : 'bg-[#70AD47]/20 text-[#70AD47]'
                  } hover:scale-110`}
                  title="Raporu e-posta ile gönder"
                >
                  <Mail size={20} />
                </button>
                
                {/* Parametreler butonu */}
                <button
                  onClick={() => setShowParams(!showParams)}
                  className={`p-2.5 rounded-full transition-all duration-500 ${
                    darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700' 
                  } hover:scale-110 ${showParams ? 'ring-2 ring-blue-400' : ''}`}
                  title="Parametreleri Göster/Gizle"
                >
                  <Sliders size={20} />
                </button>

                {/* Debug butonu */}
                <button
                  onClick={prepareDebugData}
                  className={`p-2.5 rounded-full transition-all duration-500 ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                  } hover:scale-110`}
                  title="Hesaplama Detayları"
                >
                  <Calculator size={20} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                </button>
                
                {/* Güneş Enerjisi butonu - İkonlu Buton Tasarımı */}
                <button
                  onClick={handleSolarToggle}
                  className={`p-2.5 rounded-full transition-all duration-500 flex items-center gap-2 ${
                    solarEnabled 
                      ? darkMode ? 'bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-400/50' : 'bg-yellow-100 text-yellow-600 ring-1 ring-yellow-400'
                      : darkMode ? 'bg-gray-700 text-gray-400 hover:text-yellow-200' : 'bg-gray-200 text-gray-500 hover:text-yellow-600'
                  } hover:scale-105`}
                  title={solarEnabled ? "Güneş Enerjisi Aktif" : "Güneş Enerjisi Pasif"}
                >
                  <Sun size={20} className={solarEnabled ? "animate-pulse" : ""} />
                  {/* Mobilde yer kaplamaması için sadece geniş ekranlarda veya istenirse yazı eklenebilir. 
                      Şimdilik sadece ikon daha şık duracaktır ama "Solar" yazısı da eklenebilir. */}
                  {/* <span className="text-sm font-medium hidden md:block">Solar</span> */}
                </button>
                
                {/* Silme butonu - Diğerlerinden daha ayırt edici */}
                <button
                  onClick={() => onRemove(system.id)}
                  className="p-2.5 rounded-full bg-red-50 text-red-600 hover:bg-red-500 hover:text-white transition-all duration-500 hover:scale-110"
                  title="Sistemi Sil"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Alt çizgi, başlık-buton kısmından sonra gelsin */}
            <div className={`my-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>

            {/* Kapasite bilgileri */}
            <div className="mt-2 flex flex-wrap gap-3">
              <div className={`px-3 py-2 rounded-md ${darkMode ? 'bg-[#2E75B6]/30' : 'bg-[#2E75B6]/15'} flex items-center`}>
                <span className={`${darkMode ? 'text-white' : 'text-[#1F4E79]'} font-bold text-sm`}>
                  {coolingTemperature}°C/{heatingTemperature}°C
                </span>
              </div>
              
              <div className={`px-3 py-2 rounded-md ${darkMode ? 'bg-[#70AD47]/30' : 'bg-[#70AD47]/15'} flex items-center`}>
                <span className={`${darkMode ? 'text-white' : 'text-[#385723]'} font-bold text-sm`}>
                  {heatingCapacity.toFixed(0)} kW
                </span>
              </div>
            </div>
          </div>

          {/* Ekipman resimleri ve enerji transfer bölümü */}
          <div className="relative z-10 flex items-center justify-center mb-2 mt-1">
            {/* Sol kart - Chiller */}
            <div className={`bg-gradient-to-br ${bgGradient} p-3 rounded-xl shadow-lg flex flex-col items-center justify-center w-2/5 h-48 border-2 ${borderColor}`}>
              <img 
                src={imgSrc} 
                alt={equipmentName} 
                className="h-32 w-32 object-contain mb-1"
              />
              <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {equipmentName}
              </div>
              
              {/* Sadece bu üst COP değerini bırak, alttaki div'i kaldır */}
              {type === 'chiller' && (
                <div className={`text-base font-bold mt-1 ${darkMode ? 'text-yellow-200' : 'text-blue-700'}`}>
                  COP: {chillerCop.toFixed(2)}
                </div>
              )}
            </div>
            
            {/* Orta - Enerji Transfer */}
            <EnergyTransfer 
              id="system-1" 
              darkMode={darkMode} 
              solarEnabled={solarEnabled}
              imagePath="/icons/heatpumpimage.png"
              results={results}
            />
            
            {/* Sağ kart */}
            <div className="bg-gradient-to-br from-[#1F4E79]/20 to-[#7030A0]/40 dark:from-[#1F4E79]/30 dark:to-[#7030A0]/30 p-3 rounded-xl shadow-lg flex flex-col items-center justify-center w-2/5 h-48 border-2 border-[#1F4E79]/50 dark:border-[#1F4E79]/70 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#7030A0]/10 to-[#2E75B6]/10 dark:from-[#7030A0]/20 dark:to-[#2E75B6]/20 animate-pulse-slow"></div>
              
              <img 
                src={clientEquipment.imgSrc}
                alt={clientEquipment.name} 
                className="h-32 w-32 object-contain mb-1 relative z-10" 
              />
              <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'} relative z-10`}>
                {clientEquipment.name}
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'} relative z-10`}>
                {clientEquipment.description}
              </div>
            </div>
          </div>

          {/* Yıllık Tasarruf Kartı */}
          <div className="relative z-10 flex justify-center mt-6 mb-4">
             <div className={`w-full max-w-sm px-6 py-4 rounded-xl shadow-lg border ${darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white/60 border-gray-200'} backdrop-blur-sm`}>
                <h3 className={`text-center text-lg font-bold mb-3 pb-2 border-b ${darkMode ? 'text-white border-gray-700' : 'text-gray-800 border-gray-200'}`}>
                  Yıllık Tasarruf
                </h3>
                
                <div className="flex flex-col gap-3">
                  {/* Enerji */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Enerji:</span>
                      <span className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {results ? `${results.energy.toFixed(1)} MWh` : '-'}
                      </span>
                  </div>

                  {/* CO2 */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>CO₂ Azaltımı:</span>
                      <span className={`font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                         {results ? (solarEnabled ? `${Math.round(results.co2 * 0.7)} Ton` : `${Math.round(results.co2)} Ton`) : '-'}
                      </span>
                  </div>

                  {/* TL */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Kazanç (TL):</span>
                      <span className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {results ? `${Math.round(results.savings * results.euroRate * 1000).toLocaleString('tr-TR')} TL` : '-'}
                      </span>
                  </div>

                  {/* Euro */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Kazanç (€):</span>
                      <span className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                         {results ? `${Math.round(results.savings * 1000).toLocaleString('tr-TR')} €` : isLoading ? '...' : '-'}
                      </span>
                  </div>
                  
                  {/* Ağaç Kazanımı - Yeni eklenen satır */}
                  <div className="flex justify-between items-center pt-1 relative">
                      <span className={`font-medium flex items-center gap-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Ağaç Kazanımı:
                        <button
                          id={`tree-info-button-${system.id}`}
                          type="button"
                          aria-label="Ağaç kazanımı hakkında bilgi"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showTreeInfo) {
                              setShowTreeInfo(false);
                              setTreeInfoPos(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              // Tooltip yaklaşık 80px yüksekliğinde + 8px boşluk.
                              // Butonun ÜSTÜNDE açılması için top'tan yüksekliği + boşluğu çıkarıyoruz.
                              setTreeInfoPos({
                                top: rect.top - 88,
                                left: rect.left
                              });
                              setShowTreeInfo(true);
                            }
                          }}
                          className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${
                            darkMode
                              ? 'text-gray-400 hover:text-green-400 hover:bg-gray-700'
                              : 'text-gray-500 hover:text-green-700 hover:bg-gray-200'
                          }`}
                        >
                          <Info size={14} />
                        </button>
                      </span>
                      <span className={`font-bold ${darkMode ? 'text-green-500' : 'text-green-700'}`}>
                         {results ? (solarEnabled
                           ? `${Math.round((results.co2 * 0.7) / 0.025).toLocaleString('tr-TR')} Adet`
                           : `${Math.round(results.co2 / 0.025).toLocaleString('tr-TR')} Adet`)
                           : '-'}
                      </span>

                      {/* Ağaç kazanımı bilgi balonu - Portal ile body'ye render edilir (transform'lu parent'ları atlar) */}
                      {showTreeInfo && treeInfoPos && createPortal(
                        <div
                          id={`tree-info-tooltip-${system.id}`}
                          role="tooltip"
                          style={{
                            position: 'fixed',
                            top: `${treeInfoPos.top}px`,
                            left: `${treeInfoPos.left}px`,
                            zIndex: 9999
                          }}
                          className={`w-72 max-w-[calc(100vw-2rem)] p-3 rounded-lg shadow-2xl text-xs leading-relaxed border ${
                            darkMode
                              ? 'bg-gray-800 text-gray-200 border-gray-700'
                              : 'bg-white text-gray-700 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p>
                              Yetişkin bir kızıl çam ağacının yıllık CO2 emisyon değeri referans alınmıştır.
                            </p>
                            <button
                              type="button"
                              aria-label="Kapat"
                              onClick={() => {
                                setShowTreeInfo(false);
                                setTreeInfoPos(null);
                              }}
                              className={`shrink-0 inline-flex items-center justify-center rounded-full p-0.5 ${
                                darkMode
                                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                          {/* Ok işareti - balonun ALTINDA, butona doğru bakar */}
                          <span
                            style={{ position: 'absolute' }}
                            className={`-bottom-1.5 left-3 w-3 h-3 rotate-45 border-r border-b ${
                              darkMode
                                ? 'bg-gray-800 border-gray-700'
                                : 'bg-white border-gray-200'
                            }`}
                          />
                        </div>,
                        document.body
                      )}
                  </div>

                  {/* Yatırım Maliyeti - Isıl kapasite × 240 EUR × kur */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2 pt-1">
                    <span className={`font-medium flex items-center gap-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Yatırım Maliyeti:
                      <button
                        id={`invest-cost-info-button-${system.id}`}
                        type="button"
                        aria-label="Yatırım maliyeti hakkında bilgi"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (showInvestCostInfo) {
                            setShowInvestCostInfo(false);
                            setInvestCostInfoPos(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setInvestCostInfoPos({
                              top: rect.top - 110,
                              left: rect.left
                            });
                            setShowInvestCostInfo(true);
                          }
                        }}
                        className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${
                          darkMode
                            ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                            : 'text-gray-500 hover:text-blue-700 hover:bg-gray-200'
                        }`}
                      >
                        <Info size={14} />
                      </button>
                    </span>
                    <span className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {results && results.heatingCapacity
                        ? `${Math.round(results.heatingCapacity * 240).toLocaleString('tr-TR')} €`
                        : '-'}
                    </span>
                  </div>

                  {/* Geri Ödeme Süresi - Yatırım / yıllık kazanç */}
                  <div className="flex justify-between items-center border-b border-gray-200/20 pb-2 pt-1">
                    <span className={`font-medium flex items-center gap-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Geri Ödeme Süresi:
                      <button
                        id={`payback-info-button-${system.id}`}
                        type="button"
                        aria-label="Geri ödeme süresi hakkında bilgi"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (showPaybackInfo) {
                            setShowPaybackInfo(false);
                            setPaybackInfoPos(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setPaybackInfoPos({
                              top: rect.top - 110,
                              left: rect.left
                            });
                            setShowPaybackInfo(true);
                          }
                        }}
                        className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${
                          darkMode
                            ? 'text-gray-400 hover:text-amber-400 hover:bg-gray-700'
                            : 'text-gray-500 hover:text-amber-700 hover:bg-gray-200'
                        }`}
                      >
                        <Info size={14} />
                      </button>
                    </span>
                    <span className={`font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      {results && results.heatingCapacity && results.savings > 0
                        ? (() => {
                            const investEur = results.heatingCapacity * 240;
                            const annualEur = results.savings * 1000; // K Euro → Euro
                            const years = investEur / annualEur;
                            if (!Number.isFinite(years) || years <= 0) return '-';
                            return `${years.toFixed(1)} yıl`;
                          })()
                        : (results && results.savings === 0 ? '∞' : '-')}
                    </span>
                  </div>

                  {/* Geri ödeme süresi bilgi balonu */}
                  {showPaybackInfo && paybackInfoPos && createPortal(
                    <div
                      id={`payback-tooltip-${system.id}`}
                      role="tooltip"
                      style={{
                        position: 'fixed',
                        top: `${paybackInfoPos.top}px`,
                        left: `${paybackInfoPos.left}px`,
                        zIndex: 9999
                      }}
                      className={`w-72 max-w-[calc(100vw-2rem)] p-3 rounded-lg shadow-2xl text-xs leading-relaxed border ${
                        darkMode
                          ? 'bg-gray-800 text-gray-200 border-gray-700'
                          : 'bg-white text-gray-700 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p>
                          <b>Toplam yatırım maliyeti (€) ÷ Yıllık net kazanç (€)</b>
                          <br /><br />
                          Yatırım maliyeti: <b>ısıl kapasite × 240 €/kW</b>.
                          Yıllık kazanç: enerji tasarrufundan gelen net kâr (TL değer, Euro'ya çevrilir).
                          <br /><br />
                          Komisyon, bakım ve işletme giderleri dahil <u>değildir</u>.
                        </p>
                        <button
                          type="button"
                          aria-label="Kapat"
                          onClick={() => {
                            setShowPaybackInfo(false);
                            setPaybackInfoPos(null);
                          }}
                          className={`shrink-0 inline-flex items-center justify-center rounded-full p-0.5 ${
                            darkMode
                              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <span
                        style={{ position: 'absolute' }}
                        className={`-bottom-1.5 left-3 w-3 h-3 rotate-45 border-r border-b ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                        }`}
                      />
                    </div>,
                    document.body
                  )}

                  {/* Yatırım maliyeti bilgi balonu */}
                  {showInvestCostInfo && investCostInfoPos && createPortal(
                    <div
                      id={`invest-cost-tooltip-${system.id}`}
                      role="tooltip"
                      style={{
                        position: 'fixed',
                        top: `${investCostInfoPos.top}px`,
                        left: `${investCostInfoPos.left}px`,
                        zIndex: 9999
                      }}
                      className={`w-72 max-w-[calc(100vw-2rem)] p-3 rounded-lg shadow-2xl text-xs leading-relaxed border ${
                        darkMode
                          ? 'bg-gray-800 text-gray-200 border-gray-700'
                          : 'bg-white text-gray-700 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p>
                          <b>Isıl kapasite (kW) × 240 €/kW</b> baz alınmıştır.
                          TL karşılığı güncel Euro kuru ile hesaplanır.
                          <br /><br />
                          Endüstriyel ısı pompası kurulumu için tipik anahtar teslim bedel referansıdır; saha şartlarına göre değişebilir.
                        </p>
                        <button
                          type="button"
                          aria-label="Kapat"
                          onClick={() => {
                            setShowInvestCostInfo(false);
                            setInvestCostInfoPos(null);
                          }}
                          className={`shrink-0 inline-flex items-center justify-center rounded-full p-0.5 ${
                            darkMode
                              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <span
                        style={{ position: 'absolute' }}
                        className={`-bottom-1.5 left-3 w-3 h-3 rotate-45 border-r border-b ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                        }`}
                      />
                    </div>,
                    document.body
                  )}
                </div>
             </div>
          </div>

          {/* Parametreler butonunu özet verilerin altına ekleyelim */}
          <div className="relative z-10 flex justify-center mt-2">
            <button
              onClick={() => setShowParams(!showParams)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                showParams 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800' 
                  : darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
              } hover:scale-105 shadow-sm`}
            >
              <Sliders size={18} />
              {showParams ? 'Parametreleri Gizle' : 'Parametreleri Göster'}
            </button>
          </div>

          {/* Tüm parametreler bölümünü butondan sonraya taşıyalım */}
          {showParams && (
            <div className={`relative z-10 mb-2 p-4 rounded-lg mt-4 transition-all duration-500 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              {/* Güneş enerjisi desteği */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <div className="flex items-center">
                      <Sun size={16} className="mr-2 text-[#FFC000]" />
                      Güneş Enerjisi Desteği
                    </div>
                  </label>
                  <div className="flex items-center">
                    <button
                      onClick={handleSolarToggle}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                        solarEnabled 
                          ? 'bg-[#FFC000]' 
                          : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                      } transition-colors duration-300`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          solarEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Parametreler için grid layout kullanarak düzenleyebiliriz */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Isıtma Kapasitesi */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-0.5">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Isıtma Kapasitesi (kW)
                    </label>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-12 text-right`}>
                      {heatingCapacity.toFixed(1)}
                    </span>
                  </div>
                  <input
                    type="range"
                    className="w-full h-5"
                    min="100"
                    max="3000"
                    step="1"
                    value={heatingCapacity}
                    onChange={(e) => handleHeatingCapacityChange(parseFloat(e.target.value))}
                  />
                </div>

                {/* Isıtılmış Su Sıcaklığı */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-0.5">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Isıtılmış Su Sıcaklığı (°C)
                    </label>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-8 text-right`}>{heatingTemperature}</span>
                  </div>
                  <input
                    type="range"
                    className="w-full h-5"
                    min="45"
                    max="95"
                    step="1"
                    value={heatingTemperature}
                    onChange={(e) => {
                      const newHeatingTemp = parseFloat(e.target.value);
                      
                      // Sıcaklığı güncelle
                      handleParamChange('heatingTemperature', newHeatingTemp);
                      
                      // Sıcaklık değiştiği için yeni heatPumpCop hesapla
                      const newHeatPumpCop = calculateCOP(coolingTemperature, newHeatingTemp, chillerCop);
                      
                      // Soğutma kapasitesini güncelle (ısıtma kapasitesi sabit)
                      const newCoolingCapacity = calculateCoolingFromHeating(heatingCapacity, newHeatPumpCop);
                      handleParamChange('coolingCapacity', newCoolingCapacity);
                    }}
                  />
                </div>

                {/* Soğutma Kapasitesi */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-0.5">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Soğutma Kapasitesi (kW)
                    </label>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-12 text-right`}>
                      {coolingCapacity.toFixed(1)}
                    </span>
                  </div>
                  <input
                    type="range"
                    className="w-full h-5"
                    min="100"
                    max="3000"
                    step="1"
                    value={coolingCapacity}
                    onChange={(e) => handleCoolingCapacityChange(parseFloat(e.target.value))}
                  />
                </div>

                {/* Soğutulmuş Su Sıcaklığı */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-0.5">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Soğutulmuş Su Sıcaklığı (°C)
                    </label>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-8 text-right`}>{coolingTemperature}</span>
                  </div>
                  <input
                    type="range"
                    className="w-full h-5"
                    min="2"
                    max="55"
                    step="1"
                    value={coolingTemperature}
                    onChange={(e) => {
                      const newCoolingTemp = parseFloat(e.target.value);
                      
                      // Sıcaklığı güncelle
                      handleParamChange('coolingTemperature', newCoolingTemp);
                      
                      // Sıcaklık değiştiği için yeni heatPumpCop hesapla
                      const newHeatPumpCop = calculateCOP(newCoolingTemp, heatingTemperature, chillerCop);
                      
                      // Soğutma kapasitesini güncelle (ısıtma kapasitesi sabit)
                      const newCoolingCapacity = calculateCoolingFromHeating(heatingCapacity, newHeatPumpCop);
                      handleParamChange('coolingCapacity', newCoolingCapacity);
                    }}
                  />
                </div>

                {/* Chiller COP - Sadece chiller tipinde gösterilir */}
                {type === 'chiller' && (
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-0.5">
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Chiller COP
                      </label>
                      <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-8 text-right`}>{chillerCop.toFixed(2)}</span>
                    </div>
                    <input
                      type="range"
                      className="w-full h-5"
                      min="0.5"
                      max="15"
                      step="0.1"
                      value={chillerCop}
                      onChange={(e) => {
                        const newChillerCop = parseFloat(e.target.value);
                        handleParamChange('chillerCop', newChillerCop);
                      }}
                    />
                  </div>
                )}

                {/* Kule Elektrik Tüketimi - Sadece coolingTower tipinde gösterilir */}
                {type === 'coolingTower' && (
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-0.5">
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Kule Elektrik Tüketimi (kW)
                      </label>
                      <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-8 text-right`}>
                        {system.towerPower ? system.towerPower.toFixed(1) : (coolingCapacity / 100).toFixed(1)}
                      </span>
                    </div>
                    <input
                      type="range"
                      className="w-full h-5"
                      min={Math.max(1, coolingCapacity / 250)}
                      max={Math.max(10, coolingCapacity / 25)}
                      step="0.1"
                      value={system.towerPower || coolingCapacity / 100}
                      onChange={(e) => {
                        const newPower = parseFloat(e.target.value);
                        handleParamChange('towerPower', newPower);
                      }}
                    />
                  </div>
                )}

                {/* Çalışma Saati */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-0.5">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Çalışma Saati
                    </label>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'} w-12 text-right`}>{workingHours}</span>
                  </div>
                  <input
                    type="range"
                    className="w-full h-5"
                    min="1000"
                    max="8760"
                    step="100"
                    value={workingHours}
                    onChange={(e) => handleParamChange('workingHours', parseFloat(e.target.value))}
                  />
                </div>
              </div>

              {/* Kullanıcı Ekipmanı Seçimi */}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Kullanıcı Ekipmanı
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {CLIENT_EQUIPMENT_TYPES.map((equipment) => (
                    <div
                      key={equipment.id}
                      onClick={() => handleClientEquipmentChange(equipment.id)}
                      className={`flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all hover:scale-105 ${
                        clientEquipmentType === equipment.id
                          ? darkMode ? 'bg-[#2E75B6] text-white' : 'bg-[#5B9BD5]/30'
                          : darkMode ? 'bg-gray-600' : 'bg-gray-200'
                      }`}
                    >
                      <img 
                        src={equipment.imgSrc} 
                        alt={equipment.name} 
                        className="h-8 w-8 object-contain mb-1" 
                      />
                      <div className="text-xs font-medium text-center">
                        {equipment.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {showEmailModal && (
          <EmailReportModal
            darkMode={darkMode}
            onClose={handleCloseEmailModal}
            onSendReport={handleSendEmail}
            systemName={system.name}
          />
        )}
      </Card>
      
      {/* Debug Popup */}
      <DebugPopup 
        isOpen={debugPopupOpen}
        onClose={() => setDebugPopupOpen(false)}
        data={debugData || {}}
        darkMode={darkMode}
      />
    </>
  );
};

export default EquipmentCard;